
// Import React and useState for form state management
import React, { useState } from "react";
// Import Hero section component
import Hero from "../components/Hero";
// Import image assets and CSS
import HeroImage from "../../assets/images/ContactUs.png";
import contactBackground from "../../assets/images/BG_ContactUs.jpg";
import "../../css/ContactUs.css";
// Import navigation and button components
import { useNavigate } from "react-router-dom";
import Button from "../components/Button";
// Import SVG icons for phone and email
import { ReactComponent as PhoneIcon } from "../../assets/icons/call.svg";
import { ReactComponent as LocationIcon } from "../../assets/icons/email.svg";


/**
 * ContactUsPage component for displaying the contact form and contact details.
 *
 * Features:
 * - Hero section with title and subtitle
 * - Privacy notice and consent
 * - Contact form with validation and submission
 * - Adviser contact details and complaints procedure
 */
function ContactUsPage() {
  // State for form fields
  const [form, setForm] = useState({
    name: "",
    number: "",
    email: "",
    message: "",
    consent: false,
  });

  // State for form errors
  const [errors, setErrors] = useState({});
  // Navigation hook
  const navigate = useNavigate();

  // Handle input changes and update form state
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear field error as user types
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validate form fields
  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (!form.number.trim()) next.number = "Number is required";
    if (!form.email.trim()) next.email = "Email is required";
    if (!form.message.trim()) next.message = "Message is required";
    if (!form.consent)
      next.consent = "Please confirm you have read the privacy policy";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!validate()) return;
      const to = process.env.REACT_APP_ADVISER_EMAIL;
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        number: form.number.trim(),
        message: form.message.trim(),
        to: (to || "").trim(),
      };
      const url = `${process.env.REACT_APP_SERVER_URL}/send-adviser-contact-confirmation`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errText = await res.text();
        console.log("API error:", errText);
        return;
      }
      // Reset form on success
      setForm({
        name: "",
        number: "",
        email: "",
        message: "",
        consent: false,
      });
      setErrors({});
      console.log("Sent successfully");
    } catch (err) {
      console.log("handleSubmit error:", err);
    }
  };


  return (
    <>
      {/* Hero section with title and subtitle */}
      <Hero
        title={"Contact us"}
        subtitle={
          "We strive to deliver a comprehensive and competitive product range for the best possible financial solutions to your needs."
        }
        backgroundImage={HeroImage}
        lightOverlay
      />

      {/* Privacy notice and consent section */}
      <section
        className="services-section"
        style={{ backgroundColor: "#A2A985CC", padding: "20px" }}
      >
        <p
          className="content"
          style={{ textAlign: "center", maxWidth: 1200, margin: "0 auto" }}
        >
          Before you supply any personal details to us via the contact us page,
          please read our IFA{" "}
          <span
            onClick={() => navigate("/privacy-policy")}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            privacy notice
          </span>
          . This notice sets out how we will process your personal data in line
          with the General Data Protection Regulations. Once you have read the
          privacy policy, please tick to confirm that you have read it and that
          you agree to Oakmont Financial Services processing your personal
          information for the purpose of contacting you. We will not use the
          details you provide us in the contact form to market to you.
        </p>
      </section>

      {/* Contact form and adviser details section */}
      <section
        className="contact-form-wrap"
        style={{
          backgroundImage: `url(${contactBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="contact-us-row">
          {/* Adviser contact details */}
          <div className="contact-us-info">
            <h2>Send Us a Message</h2>
            <div className="divider align-left" style={{ width: "100%" }}></div>
            <div className="content-large">
              Gurmeet Singh Kler{" "}
              <span className="content-small">Cert Cll (MP)</span>
              <div
                className="content-small"
                style={{ color: "var(--LimeGreen)" }}
              >
                Mortgage, Protection & Equity Adviser
              </div>
            </div>
            {/* Email contact */}
            <div className="footer-contact-item">
              <div className="footer-contact-icon">
                <LocationIcon />
              </div>
              <div className="footer-contact-details">
                <a
                  href="mailto:gurmeet@oakmontfinancialservices.co.uk"
                  className="content-large footer-contact-value"
                >
                  gurmeet@oakmontfinancialservices.co.uk
                </a>
              </div>
            </div>
            {/* Phone contact */}
            <div className="footer-contact-item">
              <div className="footer-contact-icon">
                <PhoneIcon />
              </div>
              <div className="footer-contact-details">
                <a
                  href="tel:07970 818078"
                  className="content-large footer-contact-value"
                >
                  07970 818078
                </a>
              </div>
            </div>
          </div>
          {/* Contact form */}
          <>
            <form className="contact-form" onSubmit={handleSubmit}>
              {/* Name field */}
              <div className="contact-row">
                <div className="contact-field">
                  <label className="content-small">Name:</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className={errors.name ? "has-error" : ""}
                  />
                  {errors.name ? (
                    <span className="error-text">{errors.name}</span>
                  ) : null}
                </div>
                {/* Number field */}
                <div className="contact-field">
                  <label className="content-small">Number:</label>
                  <input
                    name="number"
                    value={form.number}
                    onChange={handleChange}
                    className={errors.number ? "has-error" : ""}
                  />
                  {errors.number ? (
                    <span className="error-text">{errors.number}</span>
                  ) : null}
                </div>
              </div>
              {/* Email field */}
              <div className="contact-field">
                <label className="content-small">Email:</label>
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className={errors.email ? "has-error" : ""}
                />
                {errors.email ? (
                  <span className="error-text">{errors.email}</span>
                ) : null}
              </div>
              {/* Message field */}
              <div className="contact-field">
                <label className="content-small">Message:</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={8}
                  className={errors.message ? "has-error" : ""}
                />
                {errors.message ? (
                  <span className="error-text">{errors.message}</span>
                ) : null}
              </div>
              {/* Consent checkbox */}
              <div className="contact-consent">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={handleChange}
                  id="consent"
                />
                <label htmlFor="consent" className="content-small-italic">
                  <span style={{ color: "#d04444" }}>*</span>I confirm I have
                  read the{" "}
                  <span
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => navigate("/privacy-policy")}
                  >
                    privacy policy
                  </span>
                </label>
              </div>
              {/* Submit button */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                <Button
                  text="Submit"
                  type="submit"
                  className="contact-submit"
                />
              </div>
            </form>
            {/* Consent error message */}
            {errors.consent ? (
              <span className="error-text">{errors.consent}</span>
            ) : null}
          </>
        </div>
      </section>

      {/* Complaints procedure section */}
      <div className="service-row1 light">
        <div className="service-row-content">
          <div className="service-text" id="complaints-procedure">
            <h2 className="ContactUs">Complaints Procedure</h2>
            <div className="divider align-center"></div>
            <div className="Content">
              If you wish to register a complaint, please write to Complaints
              Officer, Rosemount Financial Solutions IFA Ltd, Rosemount House,
              2-4 Chequers Road, Basingstoke, RG21 7PU.  Telephone{" "}
              <a href="tel:01256405460">
                <strong>01256 405460</strong>
              </a>
               or email 
              <a href="mailto:complaints@rosemountfsIFA.co.uk">
                <strong>complaints@rosemountfsIFA.co.uk</strong>
              </a>
               
            </div>
            <div className="Content">
              A summary of our internal complaints handling procedures for the
              reasonable and prompt handling of complaints is available on
              request and if you cannot settle your complaint with us, you may
              be entitled to refer it to the Financial Ombudsman Service at{" "}
              <a
                href="https://www.financial-ombudsman.org.uk"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.financial-ombudsman.org.uk
              </a>
               or by contacting them on 
              <a href="tel:08000234567">0800 023 4567</a>.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}


// Export the ContactUsPage component as default
export default ContactUsPage;
